import { useState, useRef, useEffect } from "react";
import { useBiLogger } from "../bi/useBiLogger";

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  error: any;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  onstart: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionEvent) => void;
  onend: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface UseSpeechRecognitionProps {
  onTranscript: (text: string) => void;
  onInterimTranscript?: (text: string) => void;
  language?: string;
}

export const useSpeechRecognition = ({
  onTranscript,
  onInterimTranscript,
  language = "en-US",
}: UseSpeechRecognitionProps) => {
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null); // Store the instance
  const biLogger = useBiLogger();

  // Check microphone permission on initialization
  useEffect(() => {
    const checkMicrophonePermission = async () => {
      try {
        // Check if browser supports permissions API
        if (navigator.permissions) {
          const permissionStatus = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });
          if (permissionStatus.state === "denied") {
            setIsMicPermissionDenied(true);
          } else if (permissionStatus.state === "granted") {
            setIsMicPermissionDenied(false);
            setSpeechError(null);
          }
          // Listen for permission changes
          permissionStatus.onchange = () => {
            if (permissionStatus.state === "denied") {
              setIsMicPermissionDenied(true);
            } else {
              setIsMicPermissionDenied(false);
              setSpeechError(null);
            }
          };
        }
      } catch (error) {
        // Permissions API might not be available in all browsers
        console.warn("Could not check microphone permissions:", error);
      }
    };

    checkMicrophonePermission();
  }, []);

  const toggleMicrophone = () => {
    biLogger.trackUserClickMarketing({ ctaName: "Toggle Microphone" });
    if (!isListening) {
      startListening();
    } else {
      stopListening();
    }
  };

  const startListening = () => {
    // Don't reset error/permission state if we already know mic is denied
    if (!isMicPermissionDenied) {
      setSpeechError(null);
    }

    // Check if the browser supports speech recognition
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechError("Speech recognition is not supported in this browser.");
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      recognition.lang = language;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interimTranscript = "";
        let finalTranscript = "";

        // Process all results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Call appropriate callbacks
        if (finalTranscript) {
          onTranscript(finalTranscript);
        }

        if (interimTranscript && onInterimTranscript) {
          onInterimTranscript(interimTranscript);
        }
      };

      recognition.onerror = (event: SpeechRecognitionEvent) => {
        if (event.error === "not-allowed") {
          setIsMicPermissionDenied(true);
          setSpeechError(
            "Microphone access is blocked. Please enable it in your browser settings.",
          );
        } else {
          setSpeechError(`Speech recognition error: ${event.error}`);
        }
        setIsListening(false);
      };

      recognition.onend = () => {
        if (recognitionRef.current === recognition) {
          recognition.start();
        }
      };

      recognition.start();
    } catch (error) {
      setSpeechError(
        `Failed to start speech recognition: ${error instanceof Error ? error.message : String(error)}`,
      );
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null; // Clear the reference
      setIsListening(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  return {
    toggleMicrophone,
    isListening,
    speechError,
    isMicPermissionDenied,
  };
};
